import { useEffect, useState } from 'react';
import { X } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { FloatingInput } from '@/components/ui/floating-input';
import { FaviconField } from '@/components/sites/FaviconField';

import styles from './SiteConfigDrawer.module.css';

export interface SiteConfigDrawerProps {
  open: boolean;
  onClose: () => void;
  title: string;
  onTitleChange: (value: string) => void;
  subdomain: string;
  sitesDomain: string;
  favicon: string;
  onFaviconChange: (value: string) => void;
  /**
   * Used by the favicon thumbnail to absolutize relative `/images/...` paths
   * against the site's deployed host.
   */
  siteHost?: string;
}

/**
 * Right-side flyout that hosts the same field set as the wizard's "Take it
 * live" form, used post-creation to edit a site's title and favicon. The
 * subdomain is rendered as a disabled field — subdomain changes are not
 * supported yet (the subdomain is baked into the deployed Pages project's
 * routing and changing it would require coordinated rename of the project,
 * DNS record, and customer-facing URL).
 *
 * Field state is bound directly to the parent's meta — there's no separate
 * draft buffer. Persistence happens at Publish time, same as bundle edits.
 */
export const SiteConfigDrawer = ({
  open,
  onClose,
  title,
  onTitleChange,
  subdomain,
  sitesDomain,
  favicon,
  onFaviconChange,
  siteHost,
}: SiteConfigDrawerProps) => {
  // Snapshot the values at the moment the drawer opens so we can show a
  // dirty indicator when the user edits anything. Cleared on close so the
  // next open captures a fresh baseline.
  const [baseline, setBaseline] = useState<{ title: string; favicon: string } | null>(null);
  useEffect(() => {
    if (open && !baseline) {
      setBaseline({ title, favicon });
    } else if (!open && baseline) {
      setBaseline(null);
    }
  }, [open, baseline, title, favicon]);

  const isDirty =
    baseline !== null &&
    (title !== baseline.title || favicon !== baseline.favicon);

  if (!open) return null;
  return (
    <>
      <div
        className={styles.backdrop}
        role="presentation"
        onClick={onClose}
      />
      <aside
        className={styles.drawer}
        role="dialog"
        aria-modal="true"
        aria-labelledby="site-config-drawer-title"
      >
        <div className={styles.header}>
          <h2 id="site-config-drawer-title" className={styles.title}>
            Edit configuration
          </h2>
          <button
            type="button"
            className={styles.close}
            onClick={onClose}
            aria-label="Close"
          >
            <X className="h-4 w-4 stroke-[1.5]" />
          </button>
        </div>

        <div className={styles.body}>
          <FloatingInput
            id="site-config-title"
            label="Site title"
            size="md"
            value={title}
            onChange={(e) => onTitleChange(e.target.value)}
          />

          <FloatingInput
            id="site-config-subdomain"
            label="Subdomain"
            size="md"
            value={subdomain}
            disabled
            readOnly
            suffix={`.${sitesDomain}`}
          />

          <FaviconField
            value={favicon}
            onChange={onFaviconChange}
            siteHost={siteHost}
          />
        </div>

        <div className={styles.footer}>
          <Button
            type="button"
            variant="primary"
            size="default"
            onClick={onClose}
            className="relative"
          >
            Save draft
            {isDirty && (
              <span
                aria-label="Unsaved changes"
                className="absolute top-1.5 right-1.5 h-2 w-2 rounded-full bg-success ring-2 ring-foreground"
              />
            )}
          </Button>
        </div>
      </aside>
    </>
  );
};

export default SiteConfigDrawer;
